import { DateTime } from 'luxon';
import { ADJECTIVES, DATE_FORMATS, ENTRY_DIALOG_QUERY, routeConstants } from './constants';
import { Buffer } from 'buffer';
import type { LocationQueryValue, RouteParamsGeneric } from 'vue-router';

/**
 * Collapses a route query value to the single string callers expect. Vue Router surfaces a repeated param
 * (`?form=a&form=b`) as an array and a valueless one (`?form`) as null, either of which would otherwise flow into code
 * typed as `string`. First entry wins for duplicates, matching `URLSearchParams.get()`.
 * @param {LocationQueryValue | LocationQueryValue[]} value - A single `route.query` entry.
 * @returns {string | undefined} The single value, or undefined when the param is absent, valueless or empty.
 */
export const singleQueryValue = (
  value: LocationQueryValue | LocationQueryValue[] | undefined
): string | undefined => {
  const single = Array.isArray(value) ? value[0] : value;
  return single || undefined;
};

/**
 * The case identifier a dashboard route is pointing at, under whichever of three names it arrived.
 *
 * The surgeon dashboard is reached from routes that name this param `caseId`, from older links that
 * name it `id`, and from the pipeline that names it `identifier` — so both dashboards read all three
 * with the first non-empty one winning. The literal string `'undefined'` has to be rejected as well
 * as an absent param: `router.replace` with a param whose value is the JS `undefined` interpolates
 * it into the path, and the next navigation reads `'undefined'` back out as a perfectly good string.
 * @param {RouteParamsGeneric} params - `route.params` for the current dashboard route.
 * @returns {string | undefined} The case identifier, or undefined when the route names no real case.
 */
export const routeCaseId = (params: RouteParamsGeneric): string | undefined => {
  for (const name of ['caseId', 'id', 'identifier'] as const) {
    const value = params[name];
    const single = Array.isArray(value) ? value[0] : value;
    if (single && single !== 'undefined') {
      return single;
    }
  }
  return undefined;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validateEmail = (input: string) => EMAIL_PATTERN.test(input);

const MOBILE_PATTERN = /^\+\d{1,3}[- ]?\d{1,14}([- ]?\d{1,13})*$/;
export const validatePhone = (phoneNumber: string) => MOBILE_PATTERN.test(phoneNumber);

export const formattedDate = (date: Date, format: string) => DateTime.fromJSDate(date).toFormat(format);

export const generateHexIdentifier = (length: number) =>
  [...Array(length)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

export const getRandomAdjective = () => ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];

const unscramble = (scrambled: string): string => {
  const buffer = Buffer.from(scrambled, 'hex');
  const [t0, t1, r0, r1] = [0, 6, 6, 8];
  const dr = r1 - r0;

  for (let i = 0; i < t1 - t0; i++) {
    buffer[t0 + i] = (0x100 + buffer[t0 + i] - buffer[r0 + (i % dr)]) % 0x100;
  }

  return buffer.toString('hex');
};

export const reverse = (s: string): string => s.split('').reverse().join('');

export const createEntryIdentifier = (surveyIdentifier: string): string => {
  const hexTimestamp = reverse(Date.now().toString(16)).padEnd(12, '0');
  const unscrambledSurveyIdentifier = unscramble(surveyIdentifier);

  return reverse(hexTimestamp) + unscrambledSurveyIdentifier + '0000';
};

export const jsToPrimeVueDateFormat = (jsFormat?: string): string | undefined => {

  if (!jsFormat) {
    return undefined;
  }

  // Define a map for JavaScript to PrimeVue formats
  const conversionMap: Record<string, string> = {
    'yyyy': 'yy',   // 4-digit year -> full year
    'yy': 'y',      // 2-digit year -> last two digits of the year
    'MMMM': 'MM',   // Full month name -> full month name (PrimeVue)
    'MMM': 'M',     // Short month name -> short month name (PrimeVue)
    'MM': 'mm',     // 2-digit month -> month with leading zero
    'M': 'm',       // 1-digit month -> month without leading zero
    'dd': 'dd',     // 2-digit day -> day with leading zero
    'd': 'd',       // 1-digit day -> day without leading zero
    'DDD': 'DD',    // Full day name -> full day name (PrimeVue)
    'D': 'D',       // Short day name -> short day name (PrimeVue)
    'ooo': 'oo',    // Day of year with leading zeros -> 3-digit day of year
    'o': 'o'       // Day of year without leading zeros -> day of year
  };

  // Replace the JS format components with PrimeVue equivalents
  return jsFormat.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d|DDD|D|ooo|o/g, (match: string) => {
    return conversionMap[match] || match;
  });
};

export function getFormattedDateList() {
  const currentDate = DateTime.now();

  return DATE_FORMATS.map(formatGroup => ({
    label: formatGroup.label,
    items: formatGroup.items.map(format => {
      return {
        label: `${format.value} (${currentDate.toFormat(format.value)})`,
        value: format.value
      };
    })
  }));
}

// Converts seconds since epoch (UTC) to a JS Date object for display purposes
export function getUTCJSDate(seconds: number) {
  return DateTime.fromSeconds(seconds, { zone: 'utc' })
    .startOf('minute') // Sets seconds and milliseconds to 0
    .setZone('local', { keepLocalTime: true })
    .toJSDate();
}

// Converts a JS Date object to seconds since epoch (UTC) to store in the database
export function getUTCSeconds(date: Date) {
  return DateTime.fromJSDate(date)
    .setZone('utc', { keepLocalTime: true })
    .toSeconds();
}

export function getUTCSecondsAtStartOfDay(date: Date) {
  return DateTime.fromJSDate(date)
    .startOf('day')
    .setZone('utc', { keepLocalTime: true })
    .toSeconds();
}

export function getOnlyTimeSeconds(date: Date) {
  const dt = DateTime.fromJSDate(date);
  const startOfDay = dt.startOf('day');
  return dt.diff(startOfDay, 'seconds').seconds;
}

export function getTimeFromSeconds(seconds: number) {
  return DateTime.now()
    .startOf('day')
    .plus({ seconds })
    .toJSDate();
}

export const getLocalTimezoneDetails = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    timezone: `${timezone} ${getFormattedOffset()}`
  };
};

export const getSystemTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getFormattedOffset = (): string => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes > 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
};

export const getRandomRgbColor = () => {
  const randomValue = () => Math.floor(Math.random() * 256); // Generate random number between 0 and 255 for RGB values
  const r = randomValue();
  const g = randomValue();
  const b = randomValue();

  return `rgba(${r}, ${g}, ${b})`;
};

export const addOpacityToRgb = (rgbColor: string, opacity = 1) => {
  // Extract the RGB values using a regular expression
  const rgbValues = rgbColor.match(/\d+/g);

  if (!rgbValues || rgbValues.length < 3) {
    throw new Error('Invalid RGB color format');
  }

  // Ensure opacity is between 0 and 1
  const a = Math.max(0, Math.min(opacity, 1));

  // Return the color in RGBA format
  return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${a})`;
};

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const convertStringHoursToNumberHours = (timepoint?: string): number => {
  if (!timepoint) return NaN;
  const match = timepoint.match(/(-?\d+)\s*(mins|hours?)/);
  if (!match) return NaN;

  const value = parseFloat(match[1]);
  const unit = match[2];

  return unit.includes('min') ? value / 60 : value;
};

export const getHoursString = (num: number) => {
  const hours = Math.trunc(num);
  const minutes = Math.round((Math.abs(num) % 1) * 60);

  const hourLabel = Math.abs(hours) === 1 ? 'hour' : 'hours';

  if (minutes === 0) {
    return `${hours} ${hourLabel}`;
  } else if (num >= 0) {
    return `${hours > 0 ? hours + ' ' + hourLabel + ' ' : ''}${minutes} mins`;
  } else {
    return `${hours !== 0 ? hours + ' ' + hourLabel + ' ' : ''}-${minutes} mins`;
  }
};

export const roundNumber = (num: number, decimalPlaces: number, mode = 'normal') => {
  const factor = Math.pow(10, decimalPlaces);

  if (mode === 'up') {
    return Math.ceil(num * factor) / factor;
  } else if (mode === 'down') {
    return Math.floor(num * factor) / factor;
  } else {
    return Math.round(num * factor) / factor; // Default: Standard rounding
  }
};

export function shiftTimestampByDateShift(timestamp: number, dateShift: number) {
  return DateTime
    .fromSeconds(timestamp)
    .plus({ days: dateShift })
    .toSeconds();
}

type ElapsedTimeTextOptions = {
  /**
   * Drop the word "Refreshed" and spell the unit as a letter: "30m ago", not
   * "Refreshed 30 minute(s) ago".
   *
   * For the small-phone widths where the sentence, the case dropdown and the refresh button cannot
   * share one line. The dashboard toolbar wraps rather than overflow, which put the button on a
   * line of its own below the case selector; the short form keeps the row intact down past 320px.
   * Pair it with `useIsNarrow()` so the string and the layout change at the same width.
   */
  compact?: boolean;
};

export function elapsedTimeText(
  now: DateTime,
  lastRefreshed: DateTime,
  options: ElapsedTimeTextOptions = {}
) {
  const diff = now
    .diff(lastRefreshed, ['minutes', 'hours'])
    .toObject();
  const compact = options.compact ?? false;

  if (diff.hours && diff.hours >= 1) {
    const hours = Math.floor(diff.hours);
    return compact ? `${hours}h ago` : `Refreshed ${hours} hour(s) ago`;
  } else if (diff.minutes && diff.minutes >= 1) {
    const minutes = Math.floor(diff.minutes);
    return compact ? `${minutes}m ago` : `Refreshed ${minutes} minute(s) ago`;
  } else {
    return compact ? `Just now` : `Refreshed just now`;
  }
}

export const DashboardCohortFilters = [
  {
    id: 0,
    label: 'All livers',
    filters: [
      {
        label: 'All data',
        value: 'all',
        key: 'All_livers',
        selected: true
      }
    ]
  },
  {
    id: 1,
    label: 'Donor type',
    filters: [
      {
        label: 'DCD',
        value: 'DCD',
        key: 'Donor_type',
        selected: false
      },
      {
        label: 'DBD',
        value: 'DBD',
        key: 'Donor_type',
        selected: false
      }
    ]
  },
  {
    id: 2,
    label: 'Donor age',
    filters: [
      {
        label: 'Younger than 60',
        value: '<60yo',
        key: 'Donor_age_group',
        selected: false
      },
      {
        label: 'Older than 60',
        value: '>=60yo',
        key: 'Donor_age_group',
        selected: false
      }
    ]
  },
  {
    id: 3,
    label: 'Steatosis',
    filters: [
      {
        label: 'Macro < 10%',
        value: '<10%',
        key: 'Donor_macrosteatosis',
        selected: false
      },
      {
        label: 'Macro 10-30%',
        value: '10-30%',
        key: 'Donor_macrosteatosis',
        selected: false
      }
    ]
  },
  {
    id: 4,
    label: 'NRP',
    filters: [
      {
        label: 'NRP - Yes',
        value: 'NRP_Yes',
        key: 'Donor_NRP',
        selected: false
      },
      {
        label: 'NRP - No',
        value: 'NRP_No',
        key: 'Donor_NRP',
        selected: false
      }
    ]
  },
  {
    id: 5,
    label: 'Transplanted',
    filters: [
      {
        label: 'Transplanted - Yes',
        value: 'Transplanted_Yes',
        key: 'Transplanted',
        selected: false
      }
    ]
  },
  {
    id: 6,
    label: 'EAD',
    filters: [
      {
        label: 'EAD - Yes',
        value: 'EAD_Yes',
        key: 'EAD',
        selected: false
      },
      {
        label: 'EAD - No',
        value: 'EAD_No',
        key: 'EAD',
        selected: false
      }
    ]
  },
  {
    id: 7,
    label: 'Ischemic Cholangiopathy',
    filters: [
      {
        label: 'IC - Yes',
        value: 'IC_Yes',
        key: 'Ischemic_Cholangiopathy',
        selected: false
      },
      {
        label: 'IC - No',
        value: 'IC_No',
        key: 'Ischemic_Cholangiopathy',
        selected: false
      }
    ]
  },
  {
    id: 8,
    label: 'PNF',
    filters: [
      {
        label: 'PNF - No',
        value: 'PNF_No',
        key: 'PNF',
        selected: false
      }
    ]
  },
  {
    id: 9,
    label: 'Graft Loss',
    filters: [
      {
        label: 'GL - Yes',
        value: 'GL_Yes',
        key: 'Graft_Loss',
        selected: false
      },
      {
        label: 'GL - No',
        value: 'GL_No',
        key: 'Graft_Loss',
        selected: false
      }
    ]
  },
  {
    id: 10,
    label: 'Death-Censored Graft Loss',
    filters: [
      {
        label: 'DCGL - Yes',
        value: 'DCGL_Yes',
        key: 'Death_Censored_Graft_Loss',
        selected: false
      }
    ]
  }
];

export const createRandomCaseLabel = () => {
  const timestamp = DateTime.now().toFormat('yyyyMMddHHmmss');
  return `Case_${timestamp}`;
};

export const delayedPromise = (ms: number) => new Promise<void>(resolve => setTimeout(() => resolve(), ms));

export const formatTimeSpent = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || seconds === 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs} ${secs === 1 ? 'second' : 'seconds'}`);
  }

  return parts.join(' ');
};

export const isValidJson = (str: string) => {
  if (typeof str !== 'string') {
    return false;
  }
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * Transforms a Teamscope deep link URL into a Studypages data entry path.
 *
 * The target is the pipeline with the entry dialog open, not a nested case/form/entry path — the
 * entry is identified by query parameters, which is what `entryDialogContext` in `DataSection.vue`
 * reads to restore the dialog on a deep link or a refresh:
 *
 *   /study/<studyId>/dash/data/cases/pipeline?formId=<...>&caseId=<...>&entryId=<...>
 *
 * @param {string} deepLinkUrl - The incoming URL from the push notification.
 * @returns {string | undefined} The transformed path.
 */
/**
 * The study a deep link points at, if any.
 *
 * Separate from `transformDeepLink` because a caller sometimes needs to know
 * which study a link belongs to without building a route for it — a foreground
 * push has to be matched against the selected study before it is shown.
 * @param {string} deepLinkUrl - The absolute deep link as delivered.
 * @returns {string | undefined} The `studyId` param, or undefined when the url carries none or does not parse.
 */
export const deepLinkStudyId = (deepLinkUrl: string): string | undefined => {
  try {
    return new URL(deepLinkUrl).searchParams.get('studyId') ?? undefined;
  } catch {
    // A malformed url is not an error here: the caller's fallback is simply to
    // treat the notification as unscoped.
    return undefined;
  }
};

export const transformDeepLink = (deepLinkUrl: string): string | undefined => {
  try {
    const url = new URL(deepLinkUrl);
    const params = url.searchParams;

    const studyId = params.get('studyId');
    if (!studyId) {
      // If there's no studyId, we can't construct a valid path.
      return undefined;
    }

    const path = `/study/${studyId}/dash/${routeConstants.DATA.path}/${routeConstants.CASES_PIPELINE.path}`;

    // Carried across as query parameters under the names the dialog looks for. A link without a
    // formId still resolves — it just lands on the pipeline with no dialog open.
    const query = new URLSearchParams();
    for (const [source, target] of [
      ['formId', ENTRY_DIALOG_QUERY.form],
      ['caseId', ENTRY_DIALOG_QUERY.case],
      ['entryId', ENTRY_DIALOG_QUERY.entry]
    ] as const) {
      const value = params.get(source);
      if (value) query.set(target, value);
    }

    // `toString()` rather than `query.size`: Safari only shipped `size` in 17, and there it reads
    // `undefined`, which would silently drop every query param and break the deep link.
    const search = query.toString();
    return search ? `${path}?${search}` : path;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Failed to parse or transform deep link URL:', error);
    return undefined;
  }
};

const DEFAULT_ALLOWED_REDIRECT_DOMAINS = [
  'studypages.com',
  'teamscope.co'
];

/**
 * Checks whether a redirect URL is safe to assign to window.location.
 * Accepts only same-origin URLs, or https URLs whose hostname is an allowed
 * domain or a subdomain of one (override the domain list via comma-separated
 * VITE_APP_ALLOWED_REDIRECT_HOSTS). The dot-boundary check keeps lookalike
 * hosts (studypages.com.evil.com, evilstudypages.com) rejected.
 * @param {string} urlString - The untrusted URL (e.g. from an OAuth state param).
 * @returns {boolean} True if the URL is safe to redirect to.
 */
export const isAllowedRedirectUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    if (url.origin === window.location.origin) {
      return true;
    }
    if (url.protocol !== 'https:') {
      return false;
    }
    const envDomains = (import.meta.env.VITE_APP_ALLOWED_REDIRECT_HOSTS as string | undefined)
      ?.split(',')
      .map(domain => domain.trim().toLowerCase())
      .filter(Boolean);
    const allowedDomains = envDomains?.length ? envDomains : DEFAULT_ALLOWED_REDIRECT_DOMAINS;
    const hostname = url.hostname.toLowerCase();
    return allowedDomains.some(
      domain => hostname === domain || hostname.endsWith(`.${domain}`)
    );
  } catch (error) {
    return false;
  }
};

/**
 * The letters to show in place of an avatar that is missing or failed to load. The initial of the
 * first name and of the second, so "Ada Lovelace" reads AL and "Ada" reads A.
 *
 * A name that isn't there gets an empty string rather than a placeholder. What an unnamed person's
 * avatar shows is the caller's decision, not this function's.
 */
export const nameInitials = (name?: string | null): string => {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) {
    return '';
  }
  return `${words[0].charAt(0)}${words[1]?.charAt(0) ?? ''}`.toUpperCase();
};
